import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { 
  ChevronLeft,
  ChevronRight,
  Save,
  BarChart,
  AlertCircle,
  Search,
  Download,
  Merge,
  Trash2,
  RefreshCw,
  Sparkles
} from "lucide-react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAppSelector } from '@/hooks/useAppSelector';
import { invokeEdgeFunction } from "@/utils/edgeFunctionUtils";
import { PricingPageLink } from './PricingPageLink';
import AddMetricDialog from './AddMetricDialog';

interface CompanyMetricsReviewProps {
  onClose: () => void;
}

type Modality = "Flat" | "License" | "Credit" | "Usage" | "Threshold" | "Unknown" | null;

interface MetricItem {
  id: string;
  display_name: string;
  pricing_metric_id: string;
  sample_values: string[] | null;
  modality: Modality;
  subcategory: string | null;
}

interface MetricsExportItem {
  slug: string;
  display_name: string;
  sample_values: string[] | null;
  modality: Modality;
  subcategory: string | null;
  pricing_metric_name: string | null;
}

interface MergeMetricsDialogProps {
  metrics: MetricItem[];
  onMerge: (keepMetricId: string, mergeMetricId: string) => Promise<void>;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const MODALITY_OPTIONS: { value: Modality; label: string }[] = [
  { value: "Flat", label: "Flat" },
  { value: "License", label: "License" },
  { value: "Credit", label: "Credit" },
  { value: "Usage", label: "Usage" },
  { value: "Threshold", label: "Threshold" },
  { value: "Unknown", label: "Unknown" },
  { value: null, label: "Not specified" }
];

const MergeMetricsDialog = ({ metrics, onMerge, isOpen, onOpenChange }: MergeMetricsDialogProps) => {
  const [keepMetricId, setKeepMetricId] = useState<string>("");
  const [mergeMetricId, setMergeMetricId] = useState<string>("");
  const [isMerging, setIsMerging] = useState(false);
  const { toast } = useToast();

  const handleMerge = async () => {
    if (!keepMetricId || !mergeMetricId) {
      toast({
        variant: "destructive",
        title: "Selection required",
        description: "Please select both metrics to merge."
      });
      return;
    }

    if (keepMetricId === mergeMetricId) {
      toast({
        variant: "destructive",
        title: "Invalid selection",
        description: "Please select two different metrics to merge."
      });
      return;
    }

    setIsMerging(true);
    try {
      await onMerge(keepMetricId, mergeMetricId);
      onOpenChange(false);
    } catch (error) {
      console.error('Error merging metrics:', error);
      toast({
        variant: "destructive",
        title: "Merge failed",
        description: error.message || "An error occurred while merging metrics."
      });
    } finally {
      setIsMerging(false);
      setKeepMetricId("");
      setMergeMetricId("");
    }
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Merge Metrics</DialogTitle>
        <DialogDescription>
          Select two metrics to merge. All references to the second metric will be updated to point to the first metric.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="keepMetric" className="text-sm font-medium">Keep this metric:</label>
          <Select value={keepMetricId} onValueChange={setKeepMetricId}>
            <SelectTrigger id="keepMetric">
              <SelectValue placeholder="Select metric to keep" />
            </SelectTrigger>
            <SelectContent>
              {metrics.map(metric => (
                <SelectItem key={`keep-${metric.id}`} value={metric.id}>
                  {metric.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="mergeMetric" className="text-sm font-medium">Merge this metric (will be deleted):</label>
          <Select value={mergeMetricId} onValueChange={setMergeMetricId}>
            <SelectTrigger id="mergeMetric">
              <SelectValue placeholder="Select metric to merge" />
            </SelectTrigger>
            <SelectContent>
              {metrics.map(metric => (
                <SelectItem key={`merge-${metric.id}`} value={metric.id}>
                  {metric.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isMerging}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleMerge}
          disabled={isMerging || !keepMetricId || !mergeMetricId}
          className="flex items-center gap-2"
        >
          {isMerging ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
          ) : (
            <Merge className="h-4 w-4 mr-2" />
          )}
          Merge Metrics
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const CompanyMetricsReview = ({ onClose }: CompanyMetricsReviewProps) => {
  const [companies, setCompanies] = useState<Array<{slug: string, category?: string}>>([]);
  const [currentCompanyIndex, setCurrentCompanyIndex] = useState(0);
  const [companyMetrics, setCompanyMetrics] = useState<MetricItem[]>([]);
  const [isDeletingAllMetrics, setIsDeletingAllMetrics] = useState(false);
  const [isExtractingMetrics, setIsExtractingMetrics] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [savingMetrics, setSavingMetrics] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [updatedMetrics, setUpdatedMetrics] = useState<Record<string, Modality>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState<Array<{slug: string, category?: string}>>([]);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [isDeletingMetric, setIsDeletingMetric] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState<MetricItem | null>(null);
  const [refreshingMetrics, setRefreshingMetrics] = useState(false);
  const { toast } = useToast();
  
  const currentCompany = companies[currentCompanyIndex] || null;

  // Load unique companies from page_pricing_metrics table with pagination
  useEffect(() => {
    const fetchUniqueCompanies = async () => {
      setLoadingCompanies(true);
      try {
        console.log('🔄 [CompanyMetricsReview] Fetching all company slugs with pagination...');
        
        // Array to hold all slugs
        let allSlugs: string[] = [];
        
        // Pagination settings
        const pageSize = 1000;
        let currentPage = 0;
        let hasMoreData = true;
        
        // Fetch all slugs with pagination
        while (hasMoreData) {
          console.log(`🔄 [CompanyMetricsReview] Fetching page ${currentPage + 1} of slugs (page size: ${pageSize})`);
          
          const { data, error } = await supabase
            .from('page_pricing_metrics')
            .select('slug')
            .range(currentPage * pageSize, (currentPage + 1) * pageSize - 1)
            .order('slug');
          
          if (error) throw error;
          
          if (data && data.length > 0) {
            console.log(`✅ [CompanyMetricsReview] Retrieved ${data.length} slugs for page ${currentPage + 1}`);
            allSlugs = [...allSlugs, ...data.map(item => item.slug)];
            
            // Check if we got less than pageSize records, indicating we've reached the end
            if (data.length < pageSize) {
              hasMoreData = false;
              console.log('✅ [CompanyMetricsReview] Reached end of data');
            } else {
              currentPage++;
            }
          } else {
            hasMoreData = false;
            console.log('✅ [CompanyMetricsReview] No more data to retrieve');
          }
        }
        
        console.log(`✅ [CompanyMetricsReview] Successfully retrieved a total of ${allSlugs.length} slug records`);
        
        // Extract unique slugs (company names)
        const uniqueSlugs = Array.from(new Set(allSlugs));
        console.log(`✅ [CompanyMetricsReview] Found ${uniqueSlugs.length} unique company slugs`);
        
        // Create company objects with just the slug
        const companyList = uniqueSlugs.map(slug => ({ slug }));
        
        setCompanies(companyList);
        setFilteredCompanies(companyList);
      } catch (error) {
        console.error('❌ [CompanyMetricsReview] Error fetching unique companies:', error);
        toast({
          variant: "destructive",
          title: "Failed to load companies",
          description: "Could not load the list of companies with metrics."
        });
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchUniqueCompanies();
  }, [toast]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredCompanies(companies);
      return;
    }
    
    const filtered = companies.filter(company => 
      company.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.category && company.category.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    setFilteredCompanies(filtered);
  }, [searchTerm, companies]);

  const fetchCompanyMetrics = async (slug: string) => {
    setLoading(true);
    try {
      console.log(`🔄 [CompanyMetricsReview] Fetching all metrics for company ${slug} with pagination...`);
      
      // Array to hold all metrics data
      let allMetricsData: any[] = [];
      
      // Pagination settings
      const pageSize = 500;
      let currentPage = 0;
      let hasMoreData = true;
      
      // Fetch all metrics with pagination
      while (hasMoreData) {
        console.log(`🔄 [CompanyMetricsReview] Fetching page ${currentPage + 1} of metrics for ${slug} (page size: ${pageSize})`);
        
        const { data, error } = await supabase
          .from('page_pricing_metrics')
          .select(`
            id,
            display_name,
            pricing_metric_id,
            sample_values,
            modality,
            pricing_metrics:pricing_metric_id (
              id,
              modality,
              subcategory
            )
          `)
          .eq('slug', slug)
          .range(currentPage * pageSize, (currentPage + 1) * pageSize - 1);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          console.log(`✅ [CompanyMetricsReview] Retrieved ${data.length} metrics for page ${currentPage + 1}`);
          allMetricsData = [...allMetricsData, ...data];
          
          // Check if we got less than pageSize records, indicating we've reached the end
          if (data.length < pageSize) {
            hasMoreData = false;
            console.log('✅ [CompanyMetricsReview] Reached end of metrics data');
          } else {
            currentPage++;
          }
        } else {
          hasMoreData = false;
          console.log('✅ [CompanyMetricsReview] No more metrics data to retrieve');
        }
      }
      
      console.log(`✅ [CompanyMetricsReview] Successfully retrieved a total of ${allMetricsData.length} metrics for ${slug}`);
      
      const processedMetrics: MetricItem[] = allMetricsData.map(item => ({
        id: item.id,
        display_name: item.display_name,
        pricing_metric_id: item.pricing_metric_id,
        sample_values: item.sample_values,
        modality: item.modality as Modality,
        subcategory: item.pricing_metrics?.subcategory || null
      }));
      
      setCompanyMetrics(processedMetrics);
      setUpdatedMetrics({});
      return processedMetrics;
    } catch (error) {
      console.error('❌ [CompanyMetricsReview] Error fetching company metrics:', error);
      toast({
        variant: "destructive",
        title: "Failed to load metrics",
        description: "Could not load metrics for this company."
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Load metrics when current company changes
  useEffect(() => {
    if (!currentCompany) return;
    fetchCompanyMetrics(currentCompany.slug);
  }, [currentCompany]);

  const handlePrevious = () => {
    if (currentCompanyIndex > 0) {
      setCurrentCompanyIndex(currentCompanyIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentCompanyIndex < companies.length - 1) {
      setCurrentCompanyIndex(currentCompanyIndex + 1);
    }
  };

  const handleCompanySelect = (slug: string) => {
    const newIndex = companies.findIndex(company => company.slug === slug);
    if (newIndex !== -1) {
      setCurrentCompanyIndex(newIndex);
      setSearchTerm(''); // Clear search after selection
    }
  };

  const handleModalityChange = (metricId: string, modality: Modality) => {
    setUpdatedMetrics({
      ...updatedMetrics,
      [metricId]: modality
    });
  };

  const saveChanges = async () => {
    if (Object.keys(updatedMetrics).length === 0) {
      toast({ description: "No changes to save" });
      return;
    }

    setSavingMetrics(true);
    try {
      console.log("Saving updates:", updatedMetrics);
      
      const updates = Object.entries(updatedMetrics).map(([metricId, modality]) => 
        supabase
          .from('page_pricing_metrics')
          .update({ modality })
          .eq('id', metricId)
      );
      
      const results = await Promise.all(updates);
      console.log("Update results:", results);
      
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        throw new Error(`${errors.length} metrics failed to update`);
      }
      
      toast({
        title: "Metrics updated",
        description: `Successfully updated ${Object.keys(updatedMetrics).length} metrics`
      });
      
      const { data, error } = await supabase
        .from('page_pricing_metrics')
        .select(`
          id,
          display_name,
          pricing_metric_id,
          sample_values,
          modality,
          pricing_metrics:pricing_metric_id (
            id,
            modality,
            subcategory
          )
        `)
        .eq('slug', currentCompany.slug);
      
      if (error) throw error;
      
      const processedMetrics: MetricItem[] = data.map(item => ({
        id: item.id,
        display_name: item.display_name,
        pricing_metric_id: item.pricing_metric_id,
        sample_values: item.sample_values,
        modality: item.modality as Modality,
        subcategory: item.pricing_metrics?.subcategory || null
      }));
      
      setCompanyMetrics(processedMetrics);
      setUpdatedMetrics({});
    } catch (error) {
      console.error('Error saving metrics:', error);
      toast({
        variant: "destructive",
        title: "Failed to save changes",
        description: error.message || "An error occurred while saving metrics."
      });
    } finally {
      setSavingMetrics(false);
    }
  };

  const handleMergeMetrics = async (keepMetricId: string, mergeMetricId: string) => {
    try {
      toast({
        description: "Merging metrics..."
      });
      
      const keepMetric = companyMetrics.find(m => m.id === keepMetricId);
      const mergeMetric = companyMetrics.find(m => m.id === mergeMetricId);
      
      if (!keepMetric || !mergeMetric) {
        throw new Error("Selected metrics not found");
      }
      
      console.log(`Merging metrics: keeping ${keepMetric.display_name}, merging ${mergeMetric.display_name}`);
      
      const allSampleValues = [
        ...(keepMetric.sample_values || []),
        ...(mergeMetric.sample_values || [])
      ].filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
      
      // First, update the keeper metric with merged sample values
      const { error: updateError } = await supabase
        .from('page_pricing_metrics')
        .update({ sample_values: allSampleValues })
        .eq('id', keepMetricId);
      
      if (updateError) throw updateError;
      
      // Get all mappings for the metric to be merged
      const { data: mappings, error: mappingsError } = await supabase
        .from('page_pricing_metrics_map')
        .select('id, version, slug')
        .eq('page_pricing_metrics_id', mergeMetricId);
      
      if (mappingsError) throw mappingsError;
      
      // Process each mapping individually
      if (mappings && mappings.length > 0) {
        console.log(`Found ${mappings.length} mappings to process`);
        
        for (const mapping of mappings) {
          try {
            // Check if a mapping to the keeper already exists with the same version and slug
            const { data: existingMapping, error: checkExistingError } = await supabase
              .from('page_pricing_metrics_map')
              .select('id')
              .eq('page_pricing_metrics_id', keepMetricId)
              .eq('version', mapping.version)
              .eq('slug', mapping.slug)
              .maybeSingle();
              
            if (checkExistingError) {
              console.error(`Error checking existing mapping: ${checkExistingError.message}`);
              continue;
            }
            
            if (existingMapping) {
              console.log(`Mapping for version ${mapping.version} already exists for keeper, deleting duplicate mapping ${mapping.id}`);
              // Delete the duplicate mapping since we already have one for this version
              const { error: deleteMapError } = await supabase
                .from('page_pricing_metrics_map')
                .delete()
                .eq('id', mapping.id);
                
              if (deleteMapError) {
                console.error(`Error deleting duplicate mapping ${mapping.id}: ${deleteMapError.message}`);
              }
            } else {
              // Update the mapping to point to the keeper
              console.log(`Updating mapping ${mapping.id} to point to keeper ${keepMetricId}`);
              const { error: updateMapError } = await supabase
                .from('page_pricing_metrics_map')
                .update({ page_pricing_metrics_id: keepMetricId })
                .eq('id', mapping.id);
              
              if (updateMapError) {
                // If there's a foreign key violation, it's likely because the mapping already exists
                // In this case, just delete the current mapping
                if (updateMapError.message.includes('violates foreign key constraint') || 
                    updateMapError.message.includes('duplicate key value')) {
                  console.log(`Foreign key violation detected, deleting mapping ${mapping.id}`);
                  const { error: deleteMapError } = await supabase
                    .from('page_pricing_metrics_map')
                    .delete()
                    .eq('id', mapping.id);
                    
                  if (deleteMapError) {
                    console.error(`Error deleting mapping after FK violation ${mapping.id}: ${deleteMapError.message}`);
                  }
                } else {
                  console.error(`Error updating mapping ${mapping.id}: ${updateMapError.message}`);
                }
              }
            }
          } catch (mappingError) {
            console.error(`Error processing mapping ${mapping.id}: ${mappingError.message}`);
            // Continue processing other mappings
          }
        }
      }
      
      // Verify all mappings have been updated before attempting delete
      const { data: checkMappings, error: checkError } = await supabase
        .from('page_pricing_metrics_map')
        .select('id', { count: 'exact' })
        .eq('page_pricing_metrics_id', mergeMetricId);
        
      if (checkError) throw checkError;
      
      if (checkMappings && checkMappings.length > 0) {
        console.log(`Warning: ${checkMappings.length} mappings still reference the metric to be deleted.`);
        console.log(`Attempting to delete these remaining mappings before proceeding`);
        
        // Try to delete the remaining mappings
        for (const mapping of checkMappings) {
          const { error: deleteMapError } = await supabase
            .from('page_pricing_metrics_map')
            .delete()
            .eq('id', mapping.id);
          
          if (deleteMapError) {
            console.error(`Error deleting remaining mapping ${mapping.id}: ${deleteMapError.message}`);
          }
        }
      }
      
      // After handling mappings, try to delete the metric
      try {
        const { error: deleteError } = await supabase
          .from('page_pricing_metrics')
          .delete()
          .eq('id', mergeMetricId);
        
        if (deleteError) {
          console.error(`Warning: Could not delete metric ${mergeMetricId}: ${deleteError.message}`);
          toast({
            title: "Partial success",
            description: `Merged values and updated mappings, but couldn't delete the original metric due to references`,
            variant: "default"
          });
        } else {
          toast({
            title: "Metrics merged",
            description: `Successfully merged "${mergeMetric.display_name}" into "${keepMetric.display_name}"`,
            variant: "default"
          });
        }
      } catch (deleteError) {
        console.error(`Error in final delete step: ${deleteError.message}`);
      }
      
      // Refresh the metrics list regardless of full/partial success
      const { data, error } = await supabase
        .from('page_pricing_metrics')
        .select(`
          id,
          display_name,
          pricing_metric_id,
          sample_values,
          modality,
          pricing_metrics:pricing_metric_id (
            id,
            modality,
            subcategory
          )
        `)
        .eq('slug', currentCompany.slug);
      
      if (error) throw error;
      
      const processedMetrics: MetricItem[] = data.map(item => ({
        id: item.id,
        display_name: item.display_name,
        pricing_metric_id: item.pricing_metric_id,
        sample_values: item.sample_values,
        modality: item.modality as Modality,
        subcategory: item.pricing_metrics?.subcategory || null
      }));
      
      setCompanyMetrics(processedMetrics);
    } catch (error) {
      console.error('Error merging metrics:', error);
      toast({
        variant: "destructive",
        title: "Failed to merge metrics",
        description: error.message || "An error occurred while merging metrics."
      });
      throw error;
    }
  };

  const downloadCSV = async () => {
    setDownloadingCSV(true);
    try {
      toast({
        description: "Preparing metrics data for download. This might take a while for large datasets..."
      });
      
      const { data, error } = await invokeEdgeFunction('export-metrics-csv');
      
      if (error) {
        throw new Error(error.message || 'Failed to retrieve metrics data');
      }
      
      if (!data || !data.metrics) {
        throw new Error('No metrics data returned from the function');
      }
      
      const metrics = data.metrics;
      toast({
        description: `Processing ${metrics.length} metrics records...`
      });
      
      // Set headers in the specified order
      const headers = ['slug', 'modality', 'pricing_metric_id', 'display_name', 'sample_values', 'created_at', 'quarter', 'id', 'sample_prices'];
      let csvContent = headers.join(',') + '\n';
      
      metrics.forEach(item => {
        const row = [
          item.slug || '',
          item.modality || '',
          item.pricing_metric_id || '',
          item.display_name || '',
          item.sample_values ? `"${item.sample_values.join(', ')}"` : '',
          item.created_at || '',
          item.quarter || '',
          item.id || '',
          item.sample_prices ? `"${item.sample_prices.join(', ')}"` : ''
        ];
        csvContent += row.join(',') + '\n';
      });
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `metrics_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "CSV Downloaded",
        description: `Successfully exported ${metrics.length} metrics records`
      });
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: error.message || "Failed to download metrics data"
      });
    } finally {
      setDownloadingCSV(false);
    }
  };

  const runDuplicatesCleanup = async () => {
    try {
      toast({
        description: "Running duplicates cleanup..."
      });
      
      const { data, error } = await invokeEdgeFunction('cleanup-metrics-duplicates');
      
      if (error) {
        throw new Error(error.message || 'Failed to run duplicates cleanup');
      }
      
      toast({
        title: "Cleanup completed",
        description: `Processed ${data?.processed || 0} duplicate sets`
      });
      
      if (currentCompany) {
        const { data, error } = await supabase
          .from('page_pricing_metrics')
          .select(`
            id,
            display_name,
            pricing_metric_id,
            sample_values,
            modality,
            pricing_metrics:pricing_metric_id (
              id,
              modality,
              subcategory
            )
          `)
          .eq('slug', currentCompany.slug);
        
        if (error) throw error;
        
        const processedMetrics: MetricItem[] = data.map(item => ({
          id: item.id,
          display_name: item.display_name,
          pricing_metric_id: item.pricing_metric_id,
          sample_values: item.sample_values,
          modality: item.modality as Modality,
          subcategory: item.pricing_metrics?.subcategory || null
        }));
        
        setCompanyMetrics(processedMetrics);
      }
    } catch (error) {
      console.error('Error running duplicates cleanup:', error);
      toast({
        variant: "destructive",
        title: "Cleanup Failed",
        description: error.message || "Failed to run duplicates cleanup"
      });
    }
  };

  const handleDeleteMetric = async (metric: MetricItem) => {
    setMetricToDelete(metric);
  };

  const confirmDeleteMetric = async () => {
    if (!metricToDelete) return;
    
    setIsDeletingMetric(true);
    try {
      toast({
        description: `Deleting metric ${metricToDelete.display_name}...`
      });
      
      // First, delete all mappings for this metric
      const { data: mappings, error: mappingsError } = await supabase
        .from('page_pricing_metrics_map')
        .select('id')
        .eq('page_pricing_metrics_id', metricToDelete.id);
      
      if (mappingsError) {
        console.error('Error fetching mappings:', mappingsError);
        throw new Error(`Error finding associated mappings: ${mappingsError.message}`);
      }
      
      console.log(`Found ${mappings?.length || 0} mappings to delete for metric ${metricToDelete.id}`);
      
      // Delete each mapping individually to better handle errors
      if (mappings && mappings.length > 0) {
        for (const mapping of mappings) {
          const { error: deleteMapError } = await supabase
            .from('page_pricing_metrics_map')
            .delete()
            .eq('id', mapping.id);
          
          if (deleteMapError) {
            console.error(`Error deleting mapping ${mapping.id}:`, deleteMapError);
            // Continue with other mappings even if one fails
          }
        }
      }
      
      // After deleting all mappings, delete the metric itself
      const { error: deleteError } = await supabase
        .from('page_pricing_metrics')
        .delete()
        .eq('id', metricToDelete.id);
      
      if (deleteError) {
        throw new Error(`Error deleting metric: ${deleteError.message}`);
      }
      
      // Remove the deleted metric from state
      setCompanyMetrics(prevMetrics => 
        prevMetrics.filter(m => m.id !== metricToDelete.id)
      );
      
      toast({
        title: "Metric deleted",
        description: `Successfully deleted "${metricToDelete.display_name}" and its mappings`
      });
      
      setMetricToDelete(null);
    } catch (error) {
      console.error('Error deleting metric:', error);
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message || "An error occurred while deleting the metric."
      });
    } finally {
      setIsDeletingMetric(false);
    }
  };

  const handleRefreshMetrics = async () => {
    if (!currentCompany) return;
    
    setRefreshingMetrics(true);
    try {
      const metrics = await fetchCompanyMetrics(currentCompany.slug);
      
      if (metrics) {
        toast({
          title: "Metrics Refreshed",
          description: `Successfully refreshed ${metrics.length} metrics for ${currentCompany.slug}`
        });
      }
    } catch (error) {
      console.error('Error refreshing metrics:', error);
      toast({
        variant: "destructive",
        title: "Failed to refresh metrics",
        description: "Could not refresh metrics for this company."
      });
    } finally {
      setRefreshingMetrics(false);
    }
  };
  
  const handleExtractMetrics = async () => {
    if (!currentCompany) return;
    
    setIsExtractingMetrics(true);
    
    toast({
      description: `Extracting metrics for ${currentCompany.slug}...`
    });
    
    try {
      // Use the extract-metrics edge function (now fixed)
      const { data, error } = await invokeEdgeFunction('extract-metrics', {
        slug: currentCompany.slug,
        force: true
      });
      
      if (error) {
        throw new Error(error.message || 'Failed to extract metrics');
      }
      
      // Refresh metrics to get the latest data
      await handleRefreshMetrics();
      
      toast({
        title: "Metrics Extracted",
        description: `Successfully extracted ${data?.processedMetrics?.length || 0} metrics for ${currentCompany.slug}`
      });
    } catch (error) {
      console.error('Error extracting metrics:', error);
      toast({
        variant: "destructive",
        title: "Failed to extract metrics",
        description: error.message || "Could not extract metrics for this company."
      });
    } finally {
      setIsExtractingMetrics(false);
    }
  };

  const handleDeleteAllMetrics = async () => {
    if (!currentCompany) return;
    
    setIsDeletingAllMetrics(true);
    try {
      toast({
        description: `Deleting all metrics for ${currentCompany.slug}...`
      });
      
      // Try both approaches - first with the edge function, then fallback to direct DB access if needed
      let success = false;
      let deletedMetrics = 0;
      let deletedMappings = 0;
      
      try {
        console.log(`Calling delete-all-metrics edge function for slug: ${currentCompany.slug}`);
        const payload = { slug: currentCompany.slug };
        const { data, error } = await invokeEdgeFunction('delete-all-metrics', payload);
        
        if (error) {
          console.error('Error from edge function:', error);
          throw new Error(error.message || 'Edge function failed');
        }
        
        success = true;
        deletedMetrics = data?.deletedMetrics || 0;
        deletedMappings = data?.deletedMappings || 0;
      } catch (edgeFunctionError) {
        console.warn('Edge function approach failed, falling back to direct DB access:', edgeFunctionError);
        
        // Fallback: Direct database access
        // 1. Get all metrics for this company
        const { data: metricsData, error: metricsError } = await supabase
          .from('page_pricing_metrics')
          .select('id')
          .eq('slug', currentCompany.slug);
        
        if (metricsError) {
          throw new Error(`Error fetching metrics: ${metricsError.message}`);
        }
        
        if (!metricsData || metricsData.length === 0) {
          // No metrics to delete
          success = true;
          deletedMetrics = 0;
          deletedMappings = 0;
        } else {
          const metricIds = metricsData.map(metric => metric.id);
          
          // 2. Delete mappings
          const { data: mappingsData, error: mappingsError } = await supabase
            .from('page_pricing_metrics_map')
            .delete()
            .in('page_pricing_metrics_id', metricIds)
            .select('id');
          
          if (mappingsError) {
            throw new Error(`Error deleting mappings: ${mappingsError.message}`);
          }
          
          deletedMappings = mappingsData?.length || 0;
          
          // 3. Delete metrics
          const { data: deletedMetricsData, error: deleteError } = await supabase
            .from('page_pricing_metrics')
            .delete()
            .eq('slug', currentCompany.slug)
            .select('id');
          
          if (deleteError) {
            throw new Error(`Error deleting metrics: ${deleteError.message}`);
          }
          
          deletedMetrics = deletedMetricsData?.length || 0;
          success = true;
        }
      }
      
      // Clear the metrics from state
      setCompanyMetrics([]);
      
      toast({
        title: "Metrics Deleted",
        description: `Successfully deleted ${deletedMetrics} metrics and ${deletedMappings} mappings for ${currentCompany.slug}`
      });
      
      setShowDeleteAllConfirm(false);
    } catch (error) {
      console.error('Error deleting all metrics:', error);
      toast({
        variant: "destructive",
        title: "Failed to delete metrics",
        description: error.message || "Could not delete metrics for this company."
      });
    } finally {
      setIsDeletingAllMetrics(false);
    }
  };

  const handleMetricAdded = async () => {
    if (!currentCompany) return;
    await handleRefreshMetrics();
  };

  if (loadingCompanies) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-lg font-medium text-center">Loading companies...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentCompany) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-center">No companies available</p>
            <Button onClick={onClose} className="mt-4">
              Close Metrics Review
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-bold">
              {currentCompany.slug}
            </CardTitle>
            <CardDescription>
              {currentCompany.category || "No category"} • {currentCompany.employees || "Unknown size"}
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <PricingPageLink slug={currentCompany.slug} />
            <div className="relative w-full md:w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && filteredCompanies.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg border border-gray-200">
                  <ScrollArea className="max-h-60">
                    <div className="py-1">
                      {filteredCompanies.map((company) => (
                        <button
                          key={company.slug}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => handleCompanySelect(company.slug)}
                        >
                          {company.slug} {company.category ? `(${company.category})` : ''}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handlePrevious}
                disabled={currentCompanyIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {currentCompanyIndex + 1} of {companies.length}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={handleNext}
                disabled={currentCompanyIndex === companies.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : companyMetrics.length === 0 ? (
          <div className="py-8 text-center">
            <BarChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No metrics found for this company</p>
            <p className="text-sm text-muted-foreground mt-2">Use the "Extract Metrics" button above to populate metrics for this company.</p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-muted-foreground">
                {companyMetrics.length} metrics found
              </div>
              <div className="flex gap-2">
                <AddMetricDialog 
                  slug={currentCompany.slug} 
                  onMetricAdded={handleMetricAdded} 
                />
                <Button
                  variant="outline"
                  onClick={handleRefreshMetrics}
                  disabled={refreshingMetrics}
                  className="flex items-center gap-2"
                >
                  {refreshingMetrics ? (
                    <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></span>
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh Metrics
                </Button>
                <Button
                  variant="outline"
                  onClick={() => runDuplicatesCleanup()}
                  className="flex items-center gap-2"
                >
                  Auto-Clean Duplicates
                </Button>
                <Button 
                  onClick={handleExtractMetrics}
                  disabled={isExtractingMetrics}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {isExtractingMetrics ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  {isExtractingMetrics ? "Extracting..." : "Extract Metrics"}
                </Button>
                <AlertDialog open={showDeleteAllConfirm} onOpenChange={setShowDeleteAllConfirm}>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All Metrics
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete All Metrics</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete all metrics for {currentCompany?.slug}? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={isDeletingAllMetrics}>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAllMetrics}
                        className="bg-red-500 hover:bg-red-600"
                        disabled={isDeletingAllMetrics}
                      >
                        {isDeletingAllMetrics ? (
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                        ) : (
                          "Delete All"
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Dialog open={isMergeDialogOpen} onOpenChange={setIsMergeDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Merge className="h-4 w-4 mr-2" />
                      Merge Metrics
                    </Button>
                  </DialogTrigger>
                  <MergeMetricsDialog 
                    metrics={companyMetrics}
                    onMerge={handleMergeMetrics}
                    isOpen={isMergeDialogOpen}
                    onOpenChange={setIsMergeDialogOpen}
                  />
                </Dialog>
                <Button
                  variant="outline"
                  onClick={downloadCSV}
                  disabled={downloadingCSV}
                  className="flex items-center gap-2"
                >
                  {downloadingCSV ? (
                    <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></span>
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export All to CSV
                </Button>
                <Button
                  onClick={saveChanges}
                  disabled={Object.keys(updatedMetrics).length === 0 || savingMetrics}
                  className="flex items-center gap-2"
                >
                  {savingMetrics ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            </div>
            <ScrollArea className="h-[450px] rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Metric Name</TableHead>
                    <TableHead>Current Modality</TableHead>
                    <TableHead>New Modality</TableHead>
                    <TableHead>Sample Values</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companyMetrics.map((metric) => (
                    <TableRow key={metric.id}>
                      <TableCell className="font-medium">{metric.display_name}</TableCell>
                      <TableCell>
                        {metric.modality || <span className="text-muted-foreground italic">Not set</span>}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={updatedMetrics[metric.id] !== undefined 
                            ? updatedMetrics[metric.id] === null ? "null" : updatedMetrics[metric.id]
                            : metric.modality === null ? "null" : metric.modality}
                          onValueChange={(value) => handleModalityChange(
                            metric.id, 
                            value === "null" ? null : value as Modality
                          )}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select modality" />
                          </SelectTrigger>
                          <SelectContent>
                            {MODALITY_OPTIONS.map((option) => (
                              <SelectItem 
                                key={option.label} 
                                value={option.value === null ? "null" : option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[300px]">
                          {metric.sample_values && metric.sample_values.length > 0 ? (
                            metric.sample_values.map((value, idx) => (
                              <span 
                                key={idx} 
                                className="bg-slate-100 text-slate-800 text-xs px-2 py-1 rounded"
                              >
                                {value}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted-foreground italic">No samples</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteMetric(metric)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete metric</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the metric "{metric.display_name}"? 
                                This will also delete all mappings associated with this metric.
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={confirmDeleteMetric}
                                className="bg-red-500 hover:bg-red-600"
                                disabled={isDeletingMetric}
                              >
                                {isDeletingMetric ? (
                                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                                ) : (
                                  "Delete"
                                )}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="mt-4 flex justify-end">

              <Button
                onClick={saveChanges}
                disabled={Object.keys(updatedMetrics).length === 0 || savingMetrics}
                className="flex items-center gap-2"
              >
                {savingMetrics ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyMetricsReview;
